import React, { useMemo } from 'react';

import PerfectScrollbar from 'react-perfect-scrollbar';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@emotion/react';

import CmtVertical from '../../../../../@coremat/CmtNavigation/Vertical';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useDealer } from 'services/Data/DealerServices';
import { buildMenuItems } from 'services/Utilities/menuUtils';
import { useDealerPortalMenuTree } from 'services/Data/DealerPortalMenuServices';
import {
  getDealerPortalSectionName,
  getDealerPortalSectionValue,
  DealerPortalSectionEnumValue,
} from 'enums/DealerPortalSectionEnum';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { shouldShowDealerSiteNav } from 'services/Utilities/dealerSiteNavUtils';

const useStyles = makeStyles(() => ({
  perfectScrollbarSidebar: {
    height: '100%',
    transition: 'all 0.3s ease',
    '.Cmt-sidebar-fixed &, .Cmt-Drawer-container &': {
      height: '95%',
    },
    '.Cmt-modernLayout &': {
      height: '95%',
    },
    '.Cmt-miniLayout &': {
      height: '95%',
    },
    '.Cmt-miniLayout .Cmt-sidebar-content:hover &': {
      height: '95%',
    },
  },
}));

const SideBar = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { data: dealer } = useDealer();
  const theme = useTheme();
  const isCertificationRoute = location.pathname.startsWith('/dealer/certification');
  const canShowBackToPortal = dealer?.hasPortalAccess === true && isCertificationRoute;
  const accountReducer = useSelector(state => state.AccountReducer);
  const isLoggedIn = Boolean(accountReducer?.isLoggedIn);

  const showSiteNav = useMemo(() => shouldShowDealerSiteNav(location.pathname), [location.pathname]);

  const currentSection = useMemo(() => {
    const pathnameSegments = location.pathname.split('/').filter(Boolean);
    const sectionSegmentIndex = pathnameSegments.findIndex(segment => segment.toLowerCase() === 'section');
    const sectionFromPath = getDealerPortalSectionValue(pathnameSegments[sectionSegmentIndex + 1]);

    if (sectionFromPath > DealerPortalSectionEnumValue.None) {
      return sectionFromPath;
    }

    if (location.pathname.startsWith('/dealer/useful-links')) {
      return DealerPortalSectionEnumValue.UsefulLinks;
    }

    if (location.pathname.startsWith('/dealer/home-page/exterior-signage')) {
      return DealerPortalSectionEnumValue.ExteriorSignage;
    }

    if (location.pathname.startsWith('/dealer/home-page/merchandising-program')) {
      return DealerPortalSectionEnumValue.MerchandisingServices;
    }

    return DealerPortalSectionEnumValue.DisplaysToolingAndFurniture;
  }, [location.pathname]);

  const currentSectionName = useMemo(() => getDealerPortalSectionName(currentSection) || 'DisplaysToolingAndFurniture', [
    currentSection,
  ]);

  const { data: dealerPortalMenuTreeData } = useDealerPortalMenuTree(
    currentSection,
    isLoggedIn && showSiteNav && !location.pathname.startsWith('/dealer/certification'),
  );

  const menuItems = useMemo(
    () =>
      buildMenuItems({
        pathname: location.pathname,
        t,
        dealerPortalMenuTreeData,
        language: i18n.language,
        currentSectionName,
      }),
    [location.pathname, t, dealerPortalMenuTreeData, i18n.language, currentSectionName],
  );

  return (
    <PerfectScrollbar className={classes.perfectScrollbarSidebar}>
      {canShowBackToPortal && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => history.push('/dealer/home-page')}
            sx={{
              color: theme.palette.secondary.main,
              borderColor: theme.palette.secondary.main,
              '&:hover': {
                borderColor: theme.palette.secondary.main,
                bgcolor: alpha(theme.palette.secondary.main, 0.12),
              },
            }}>
            {t('certNav.backToPortal')}
          </Button>
        </Box>
      )}
      <CmtVertical menuItems={menuItems} />
    </PerfectScrollbar>
  );
};

export default SideBar;
